import type { Building } from "building-types/building/buildingTypes";
import type { DocEntityId } from "building-types/doc/docEntityId";
import type { BuildingEntityFaceHashMapsRenderSettings } from "building-types/materials/faceHashTypes";
import type { ParameterValue } from "building-types/options/parameter";
import type { Effect } from "building-types/views/effectTypes";
import type { ElevationDebugOptions } from "building-types/views/elevationAnnotationTypes";
import { ElevationNoteEntityDebugMode } from "building-types/views/elevationAnnotationTypes";
import type { GradeLineSource } from "building-types/views/GradeLineSource";
import type { ViewportRenderStyle } from "building-types/views/planSets";
import type { SheetGeometryCategoryTable } from "building-types/views/sheetGeometryCategory";
import {
  isUserDefinedAnnotationCategory,
  SheetGeometryCategory,
  viewportCategoryRenderOrder,
} from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry, SheetGeometryList } from "building-types/views/sheetGeometryTypes";
import { sheetAddGeometries, sheetGeometryListNew } from "building-types/views/sheetGeometryTypes";
import type { DimensionPrecision } from "building-types/views/viewTypes";
import type { AsyncLazy, Lazy } from "common/data/lazy";
import type { Table } from "common/data/table";
import type { LevelInches, SheetInches } from "common/types/nominalNumbersTypes";
import { aabb2Init, aabb2UnionMutable } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import type { Mat3 } from "geom/mat3";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { AnnotationParameterContext } from "./annotationParameterContext";
import type { ElevationAnnotationCommonParams } from "./elevationAnnotationCommonParams";
import type { PlanColliderObstacle } from "./interiorLabelObstacle";
import type {
  PlanGeometryGenerationDiagnostic,
  PlanGeometryGeneratorResult,
  TaggedPlanGeometry,
  GroupOfTaggedPlanGeometries,
  SheetGeometryStyler,
  PlanGeometryTiming,
  RelaxationObstacle,
} from "./planGeometrySourceTypes";
import sheetGeometryListAABB2 from "./sheetGeometryAABB2";

export type PlanGeometrySourceByLevel = { [levelId: string]: PlanGeometrySource };

export const EMPTY_PLAN_GEOMETRY_GENERATOR_RESULT = { taggedGeometries: {} };

function hasGeometriesWithRenderOrder(result: PlanGeometryGeneratorResult): boolean {
  for (const tag in result.taggedGeometries) {
    const geometries: TaggedPlanGeometry[] = result.taggedGeometries[tag];
    for (const geometry of geometries) {
      if (geometry.renderOrder !== undefined) {
        return true;
      }
    }
  }
  return false;
}

export const defaultElevationDebugOptions: ElevationDebugOptions = {
  debugElevationColliders: false,
  showElevationNoteEntities: ElevationNoteEntityDebugMode.NONE,
};

export interface ElevationGenerationContext extends ElevationAnnotationCommonParams {
  readonly building: Building;
}

export interface SectionGenerationContext {
  readonly building: Building;
  readonly sectionViewId: string;
  readonly dimensionPrecision?: DimensionPrecision;
  readonly globalParameterValues?: Table<ParameterValue>;
  /** Unset reads as false: sections draw no grade line unless the caller opts in via the `modeledGradeLine` flag. */
  readonly includeGradeLine?: boolean;
  readonly gradeLineSource?: GradeLineSource;
}

export interface PlanGenerationContextBase {
  readonly building: Building;
  readonly levelId: string;
  readonly cutPlaneHeight?: LevelInches;
  readonly stairCutPlaneHeight?: LevelInches;
  readonly wallFinishCutPlaneHeight?: LevelInches;
}

export interface PlanGenerationContext extends PlanGenerationContextBase {
  readonly dimensionPrecision?: DimensionPrecision;
  readonly viewTemplateId?: DocEntityId<"VIEW_TEMPLATE">;
  readonly globalParameterValues?: Table<ParameterValue>;
  readonly annotationParameterContext?: AnnotationParameterContext;
  readonly planSetTemplateId?: DocEntityId<"PLAN_SET_TEMPLATE">;
  readonly planSetTemplateConfigId?: DocEntityId<"PLAN_SET_TEMPLATE_CONFIG">;
  readonly renderStyleMode?: ViewportRenderStyle;
  readonly selectedReportingUnitLabelId?: string;
  readonly generationDiagnostics?: PlanGeometryGenerationDiagnostic[];
  /**
   * Categories this render relaxes, resolved from effects or the reduceOverlap default (see
   * planSetComputeServiceBase.ts). A generator can ask whether its own category is relaxed and
   * place accordingly. Unset for every other geometry source, including the built-in electrical
   * page and the Studio editor, which never build this context.
   */
  readonly relaxationInteractionMap?: SheetGeometryCategoryTable<RelaxationObstacle[]>;

  // TODO: get rid of everything below this line.
  // These are parameters that make the generated geometries plan specific,
  // or are used to break up things (e.g. SVGs) to polylines prematurely.

  /**
   * @deprecated
   * @NOTE - svgTextScale represents an incomplete migration out of
   * the deprecated PlanSettings.annotationScale, which inconsistently mutated the fontSizes
   * of some elements in the PlanGeometrySource.
   *
   * This has been converted to an svgTextScale, since this property no londer is applied to
   * and SheetGeometry.TEXT entities, but is still applied to some SheetGeometry.SVG elements
   * that have not yet been migrated. Ultimately this property should be removed as well
   */
  readonly svgTextScale: number;
  readonly planRotationRads: number;
  readonly noSettings?: boolean;
}

export type SheetGeometryGenerationContext =
  | PlanGenerationContext
  | ElevationGenerationContext
  | SectionGenerationContext;

export class PlanGeometryBuilder implements PlanGeometryGeneratorResult {
  protected readonly geometriesByTag: SheetGeometryCategoryTable<TaggedPlanGeometry[]> = {};
  protected readonly groupsByTag: SheetGeometryCategoryTable<GroupOfTaggedPlanGeometries[]> = {};

  private renderOrder: number = 0;

  constructor(private assignRenderOrder: boolean = false) {}

  public get taggedGeometries() {
    return this.geometriesByTag;
  }
  public get groups() {
    return this.groupsByTag as SheetGeometryCategoryTable<readonly GroupOfTaggedPlanGeometries[]>;
  }

  private addGeometryInternal(
    geometry: SheetGeometry,
    tag: SheetGeometryCategory,
    overrideRenderOrder: number | undefined,
  ) {
    let array = this.geometriesByTag[tag];
    if (!array) {
      array = [];
      this.geometriesByTag[tag] = array;
    }
    /**
     * @TODO - we lose all information about previous tagging here.
     * Consider making category an array
     */
    const taggedGeometry = overrideGeometryTag(geometry, tag);
    if (overrideRenderOrder !== undefined) {
      array.push({ ...taggedGeometry, renderOrder: overrideRenderOrder });
    } else if (this.assignRenderOrder) {
      ++this.renderOrder;
      array.push({ ...taggedGeometry, renderOrder: this.renderOrder });
    } else {
      array.push(taggedGeometry);
    }
  }

  public addGeometry(geometry: SheetGeometry, category: SheetGeometryCategory) {
    this.addGeometryInternal(geometry, category, undefined);
  }

  public addGroup(group: GroupOfTaggedPlanGeometries, tag: SheetGeometryCategory, addGeometriesInGroup = true) {
    let array = this.groupsByTag[tag];
    if (!array) {
      array = [];
      this.groupsByTag[tag] = array;
    }
    const groupId = `${tag}-${array.length}`;
    array.push(group);

    const geometries = group.geometries.map((g) => ({ ...g, groupId }));

    if (addGeometriesInGroup) {
      for (const g of geometries) {
        // do not assign the group tag to the geometry
        // TODO - revisit multiple categories - parent information is useful
        // and we just discard it
        this.addGeometry(g, g.category);
      }
    }
  }

  public addAllFrom(
    other: PlanGeometryGeneratorResult,
    tagReplaceMap?: SheetGeometryCategoryTable<SheetGeometryCategory>,
  ) {
    const replaceGroupId = (groupId: string | undefined) => {
      if (!groupId) {
        return groupId;
      }

      const [oldGroupTag, groupIdIndex] = groupId.split("-");

      // get the new group tag
      const newGroupTag = tagReplaceMap?.[oldGroupTag] ?? oldGroupTag;

      return `${newGroupTag}-${groupIdIndex}`;
    };

    if (hasGeometriesWithRenderOrder(other)) {
      // Previously added geometries can keep their undefined renderOrder (effectively 0).
      const baseRenderOrder = this.renderOrder;
      let renderOrderAfter = this.renderOrder;
      for (const tag in other.taggedGeometries) {
        const geometries: SheetGeometry[] = other.taggedGeometries[tag];
        const tagToUse = tagReplaceMap?.[tag] ?? tag;
        for (const geometry of geometries) {
          // Add geometries preserving their previous render order with respect to each other, but so that they're drawn on top of anything that was added previously.
          // If there are geometries with undefined render order mixed in, they'll sit between the old geometries and the newly added ones, hence 0.5 increment in that case.
          const renderOrder = baseRenderOrder + (geometry.renderOrder ?? 0.5);
          this.addGeometryInternal(
            {
              ...geometry,
              groupId: replaceGroupId(geometry.groupId),
            },
            tagToUse,
            renderOrder,
          );
          renderOrderAfter = Math.max(renderOrderAfter, renderOrder);
        }
      }
      // All geometries added after this will get assigned a renderOrder value.
      this.assignRenderOrder = true;
      this.renderOrder = renderOrderAfter;
    } else {
      for (const tag in other.taggedGeometries) {
        const geometries: SheetGeometry[] = other.taggedGeometries[tag];
        const tagToUse = tagReplaceMap?.[tag] ?? tag;
        for (const geometry of geometries) {
          this.addGeometry(
            {
              ...geometry,
              groupId: replaceGroupId(geometry.groupId),
            },
            tagToUse,
          );
        }
      }
    }
    if (other.groups) {
      for (const tag in other.groups) {
        const groups: GroupOfTaggedPlanGeometries[] = other.groups[tag];
        const tagToUse = tagReplaceMap?.[tag] ?? tag;
        for (const group of groups) {
          this.addGroup(group, tagToUse, false);
        }
      }
    }
  }
}

export interface PlanGeometryBuilderWithCollider {
  toPaperSpaceGeometry(geometry: SheetGeometry): SheetGeometry;
  toWorldSpaceGeometry(geometry: SheetGeometry): SheetGeometry;
  addPaperSpaceGeometry(geometry: SheetGeometry, category: SheetGeometryCategory, weightMultiplier?: number): void;
  addWorldSpaceGeometry(geometry: SheetGeometry, category: SheetGeometryCategory): void;
  addPaperSpaceDebugGeometry(geometry: SheetGeometry, category: SheetGeometryCategory): void;
  ptToPaperSpace(x: number, y: number): [SheetInches, SheetInches];
  xToPaperSpace(x: number): SheetInches;
  yToPaperSpace(y: number): SheetInches;
  modelSpaceToPaperSpaceTransform: Mat3;
  bb: AABB2;
  worldBB: AABB2;
  dwgCenterX: number;
  scaleFactor: number;
  viewportScale?: number;
}

export interface LazyIntermediateData {
  readonly foundationLinework?: Lazy<PlanGeometryGeneratorResult> | AsyncLazy<PlanGeometryGeneratorResult>;
  readonly exteriorDimensions?: Lazy<PlanGeometryGeneratorResult>;
  readonly interiorDimensions?: Lazy<PlanGeometryGeneratorResult>;
  readonly foundationDimensions?: Lazy<PlanGeometryGeneratorResult>;
  readonly utilityDimensions?: Lazy<PlanGeometryGeneratorResult> | AsyncLazy<PlanGeometryGeneratorResult>;
  readonly manualAnnotations?: Lazy<PlanGeometryGeneratorResult>;
  readonly manualAnnotationsAllLevels?: Lazy<PlanGeometryGeneratorResult>;
  readonly railings?: Lazy<PlanGeometryGeneratorResult>;
  readonly railingsStairOnly?: Lazy<PlanGeometryGeneratorResult>;
  readonly elevationAsyncAutoAnnotations?: AsyncLazy<PlanGeometryGeneratorResult>;
  readonly elevationAutoAnnotations?: AsyncLazy<PlanGeometryGeneratorResult>;
  readonly piers?: Lazy<PlanGeometryGeneratorResult>;
  readonly roofOverhangDimensions?: Lazy<PlanGeometryGeneratorResult>;
  readonly stairs?: Lazy<PlanGeometryGeneratorResult>;
}

type IntermediateDataIntitializer<C extends SheetGeometryGenerationContext> = (ctx: C) => LazyIntermediateData;

export type PlanGeometryGenerator<T extends SheetGeometryGenerationContext> = (
  ctx: T,
  intermediateData: LazyIntermediateData,
) => Promise<PlanGeometryGeneratorResult>;

// Used to adapt the results returned by the elevation annotation generation to PGS.
export class PlanGeometryGeneratorToSourceAdapter implements AbstractPlanGeometrySource {
  private readonly geometriesByTag: SheetGeometryCategoryTable<TaggedPlanGeometry[]> = {};
  private readonly groupsByTag: SheetGeometryCategoryTable<GroupOfTaggedPlanGeometries[]> = {};

  constructor(geometriesAndGroups: PlanGeometryGeneratorResult) {
    for (const tag in geometriesAndGroups.taggedGeometries) {
      this.geometriesByTag[tag] = geometriesAndGroups.taggedGeometries[tag];
    }
    if (geometriesAndGroups.groups) {
      for (const tag in geometriesAndGroups.groups) {
        this.groupsByTag[tag] = geometriesAndGroups.groups[tag];
      }
    }
  }

  getPlanGeometry(tag: SheetGeometryCategory): Promise<TaggedPlanGeometry[]> {
    return Promise.resolve(this.geometriesByTag[tag] ?? []);
  }

  getGroupOfGeometries(tag: SheetGeometryCategory): Promise<GroupOfTaggedPlanGeometries[]> {
    return Promise.resolve(this.groupsByTag[tag] ?? []);
  }
}

export interface SlowPlanGeometryGeneratorEvent {
  readonly error?: Error;
  readonly diagnostics?: readonly PlanGeometryGenerationDiagnostic[];
}

export type SlowPlanGeometryGeneratorCallback = (
  source: AbstractPlanGeometrySource,
  tag: SheetGeometryCategory,
  event: SlowPlanGeometryGeneratorEvent,
) => Promise<void>;

export type SlowPlanGeometryGeneratorWithLabel<T extends SheetGeometryGenerationContext> = {
  slowGenerator: PlanGeometryGenerator<T>;
  sharedGeneratorLabel: string | undefined; // when `slowGenerator` generates more than one geometry category, they all share this label.
};

export class PlanGeometrySource<C extends SheetGeometryGenerationContext = PlanGenerationContext>
  implements AbstractPlanGeometrySource
{
  private readonly geometriesByTag: SheetGeometryCategoryTable<TaggedPlanGeometry[]> = {};
  private readonly groupsByTag: SheetGeometryCategoryTable<GroupOfTaggedPlanGeometries[]> = {};
  private calledSlowGenerators = new Set<string>();
  private completedSlowGenerators = new Set<string>();
  private readonly intermediateData: LazyIntermediateData;
  private readonly _slowGeneratorTimingMs: PlanGeometryTiming = {};

  constructor(
    private readonly context: C,
    private readonly generatorsByTag: SheetGeometryCategoryTable<PlanGeometryGenerator<C>>,
    initIntermediateData: IntermediateDataIntitializer<C>,
    private readonly slowGeneratorsByTag?: SheetGeometryCategoryTable<SlowPlanGeometryGeneratorWithLabel<C>>,
    private slowGeneratorCallback?: SlowPlanGeometryGeneratorCallback,
    private slowCompletionCallback?: () => void,
  ) {
    this.intermediateData = initIntermediateData(this.context);
  }

  private async getPlanGeometryFromTableInternal<G extends SheetGeometry | GroupOfTaggedPlanGeometries>(
    requestedTag: SheetGeometryCategory,
    table: SheetGeometryCategoryTable<G[]>,
  ): Promise<G[]> {
    const lazyGeometries = table[requestedTag];
    if (lazyGeometries) {
      return lazyGeometries;
    }
    // The specification is that geometries for one tag can be generated by ONLY
    // one generator. If there is already geometry for the tag, then we must not
    // call the generator, even if those geometries were created by another
    // generator (which is likely to be another instance of the same generator function
    // associated to a different tag that it also populates).
    const generator = this.generatorsByTag[requestedTag];
    const slowGeneratorWithLabel = this.slowGeneratorsByTag?.[requestedTag];
    if (generator) {
      const geometriesAndGroups = await generator(this.context, this.intermediateData);
      addGeneratedGeometries(geometriesAndGroups, this.geometriesByTag, this.groupsByTag);
    } else if (slowGeneratorWithLabel) {
      const { slowGenerator, sharedGeneratorLabel } = slowGeneratorWithLabel;
      const outstandingLabel = sharedGeneratorLabel ?? requestedTag;
      if (!this.calledSlowGenerators.has(outstandingLabel)) {
        this.calledSlowGenerators.add(outstandingLabel);
        slowGenerator(this.context, this.intermediateData)
          .then(async (geometriesAndGroups) => {
            this.completedSlowGenerators.add(outstandingLabel);
            addGeneratedGeometries(geometriesAndGroups, this.geometriesByTag, this.groupsByTag);
            if (geometriesAndGroups.timingMs) {
              Object.assign(this._slowGeneratorTimingMs, geometriesAndGroups.timingMs);
            }
            if (geometriesAndGroups.errors) {
              // We can only have one error per slow generator at a time, so we can just use the first one.
              await this.slowGeneratorCallback?.(this, requestedTag, {
                error: geometriesAndGroups.errors[0],
                diagnostics: geometriesAndGroups.diagnostics,
              });
            } else {
              await this.slowGeneratorCallback?.(this, requestedTag, {
                diagnostics: geometriesAndGroups.diagnostics,
              });
            }
          })
          .catch(async (error: Error) => {
            this.completedSlowGenerators.add(outstandingLabel);
            await this.slowGeneratorCallback?.(this, requestedTag, { error });
          })
          .finally(async () => {
            if (this.completedSlowGenerators.size === this.calledSlowGenerators.size) {
              await this.slowCompletionCallback?.();
            }
          });
      }
      // MUST NOT EARLY RETURN HERE. The `table` must be updated with a [] while the slow generator runs,
      // so that partial renders of the plan can be done.
      //      return [];
    }
    const generatedGeometries = table[requestedTag];
    if (generatedGeometries) {
      return generatedGeometries;
    }
    // It is possible that the generator didn't generate anything.
    return (table[requestedTag] = []);

    function addGeneratedGeometries(
      geometriesAndGroups: PlanGeometryGeneratorResult,
      geometriesByTag: SheetGeometryCategoryTable<SheetGeometry[]>,
      groupsByTag: SheetGeometryCategoryTable<GroupOfTaggedPlanGeometries[]>,
    ) {
      for (const tag in geometriesAndGroups.taggedGeometries) {
        const tagToUse = tag !== SheetGeometryCategory.UNDEFINED ? (tag as SheetGeometryCategory) : requestedTag;
        geometriesByTag[tagToUse] = overrideGeometryListTags(geometriesAndGroups.taggedGeometries[tag], tagToUse);
      }
      if (geometriesAndGroups.groups) {
        for (const tag in geometriesAndGroups.groups) {
          const tagToUse = tag !== SheetGeometryCategory.UNDEFINED ? (tag as SheetGeometryCategory) : requestedTag;
          groupsByTag[tagToUse] = geometriesAndGroups.groups[tag];
        }
      }
    }
  }

  containsSlowGenerators(categories: readonly SheetGeometryCategory[]): boolean {
    for (const category of categories) {
      if (this.generatorsByTag[category]) {
        continue;
      }
      if (this.slowGeneratorsByTag?.[category]) {
        return true;
      }
    }
    return false;
  }

  get slowGeneratorTimingMs(): PlanGeometryTiming {
    return this._slowGeneratorTimingMs;
  }

  async getPlanGeometry(requestedTag: SheetGeometryCategory): Promise<TaggedPlanGeometry[]> {
    return this.getPlanGeometryFromTableInternal(requestedTag, this.geometriesByTag);
  }

  async getGroupOfGeometries(requestedTag: SheetGeometryCategory): Promise<GroupOfTaggedPlanGeometries[]> {
    return this.getPlanGeometryFromTableInternal(requestedTag, this.groupsByTag);
  }
}

// Sheet geometry filtering related types and helper functions.
// Plan types may want to filter the geometries that are generated for a tag before relaxation and rendering.

export type SheetGeometryFilter = (
  source: AbstractPlanGeometrySource,
  tag: SheetGeometryCategory,
) => Promise<readonly TaggedPlanGeometry[]>;

export type SheetGeometryGroupFilter = (
  source: AbstractPlanGeometrySource,
  tag: SheetGeometryCategory,
) => Promise<GroupOfTaggedPlanGeometries[]>;

export interface AbstractPlanGeometrySource {
  getPlanGeometry(tag: SheetGeometryCategory): Promise<readonly TaggedPlanGeometry[]>;
  getGroupOfGeometries(tag: SheetGeometryCategory): Promise<readonly GroupOfTaggedPlanGeometries[]>;
}

export class SheetGeometrySourceFilter {
  constructor(
    private readonly filters: SheetGeometryCategoryTable<SheetGeometryFilter>,
    private readonly groupFilters: SheetGeometryCategoryTable<SheetGeometryGroupFilter>,
  ) {}

  async getFilteredPlanGeometry(
    requestedTag: SheetGeometryCategory,
    source: AbstractPlanGeometrySource,
  ): Promise<readonly TaggedPlanGeometry[]> {
    const filter = this.filters[requestedTag];
    if (filter) {
      return await filter(source, requestedTag);
    }
    return await source.getPlanGeometry(requestedTag);
  }

  async getFilteredPlanGroups(
    requestedTag: SheetGeometryCategory,
    source: AbstractPlanGeometrySource,
  ): Promise<readonly GroupOfTaggedPlanGeometries[]> {
    const filter = this.groupFilters[requestedTag];
    if (filter) {
      return await filter(source, requestedTag);
    }
    return source.getGroupOfGeometries(requestedTag);
  }
}

// Helper function to filter geometries on a condition with no arguments.
// Keeps the geometry if `condition` returns true.
export function filterForCondition(condition: () => boolean) {
  return async (source: AbstractPlanGeometrySource, tag: SheetGeometryCategory) => {
    if (condition()) {
      const geometries: readonly TaggedPlanGeometry[] = await source.getPlanGeometry(tag);
      return geometries;
    }
    return [];
  };
}

// Helper function to filter geometries on a specific condition
// Keeps the geometry if `condition` returns true.
export function filterForGeometryCondition(condition: (geometry: TaggedPlanGeometry) => boolean) {
  return async (source: AbstractPlanGeometrySource, category: SheetGeometryCategory) => {
    const result: TaggedPlanGeometry[] = [];
    const geometries: readonly TaggedPlanGeometry[] = await source.getPlanGeometry(category);
    for (const g of geometries) {
      if (condition(g)) {
        result.push(g);
      }
    }
    return result;
  };
}

export function filterForGeometryGroupCondition(condition: (geometry: GroupOfTaggedPlanGeometries) => boolean) {
  return async (source: AbstractPlanGeometrySource, category: SheetGeometryCategory) => {
    const result: GroupOfTaggedPlanGeometries[] = [];
    const geometries: readonly GroupOfTaggedPlanGeometries[] = await source.getGroupOfGeometries(category);
    for (const g of geometries) {
      if (condition(g)) {
        result.push(g);
      }
    }
    return result;
  };
}

// Helper function to filter geometries on a condition that is dependent on the
// building entity that created the plan geometry.
// @NOTE - Keeps the geometry if `condition` returns false. (I.e. the condition is for skipping the geometry).
export function skipForEntityCondition<BuildingEntity>(condition: (entity: BuildingEntity) => boolean) {
  return filterForGeometryCondition((g) => !condition(g.buildingEntity));
}

// Plan geometry relaxation related types.
// Relaxation is the process of taking geometries by tag from the source and
// moving or otherwise modifying them to minimize overlaps between them.
// Currently the relaxation is still procedural. TODO: make it more declarative.

export interface Obstacles {
  obstacles?: PlanColliderObstacle[]; // TODO: remove this
  aabbObstacles?: AABB2[]; // TODO: remove this
  polylineObstacles?: PolyLine2[]; // TODO: remove this
}

export type PlanGeometryRelaxer = (
  category: SheetGeometryCategory,
  source: AbstractPlanGeometrySource,
  debugRelaxation?: boolean,
) => Promise<SheetGeometryCategoryTable<readonly SheetGeometry[]>>;

export type PlanGeometryRelaxerFactory = (
  viewScale: number | undefined,
  unrelaxedContentAABB: AABB2,
) => PlanGeometryRelaxer;

export type PlanGeometrySourceRelaxerTable = SheetGeometryCategoryTable<PlanGeometryRelaxer>;

export interface DebugPrefs {
  /**
   * when this flag is set to true, debug geometry
   * will accumulate in the scene
   */
  readonly accumulateGeometry: boolean;
  readonly entityIdSearch: boolean;
  readonly faceDiffEnabled: boolean;
  readonly faceDiffSettings: BuildingEntityFaceHashMapsRenderSettings;
  readonly debugElevationColliders: boolean;
  readonly showElevationNoteEntities: ElevationNoteEntityDebugMode;
  readonly debugFloorPlanRelaxation: boolean;
  readonly docRefDebugView: boolean;
}

interface MakePlanGeometryListProps {
  readonly planGeometrySource: AbstractPlanGeometrySource;
  readonly categoriesToRender: readonly SheetGeometryCategory[];
  readonly categoriesToRelax?: readonly SheetGeometryCategory[];
  readonly categoryRenderOrder?: readonly SheetGeometryCategory[];
  readonly relaxation?: PlanGeometryRelaxerFactory;
  readonly viewScale?: number;
  readonly filtering?: SheetGeometrySourceFilter;
  readonly styling?: SheetGeometryStyler;
  readonly debugPrefs?: DebugPrefs;
  readonly effects?: Table<Effect>;
}

// The main function to use to generate a plan rendering from a plan geometry source.
// The `categories` are the categories that will be generated and rendered.
// The `renderOrder` is the order in which the categories will be generated, filtered, relaxed, and rendered
//      for a particular plan type. Lazy generated dependencies may make some tags be generated
//      out of order. However the rendering will respect this order.
// The `relaxation` table is the plan type specific way to relax the geometries for each category.
// The `filtering` table is the plan type specific way to filter the geometries for each category.
export async function makePlanGeometryList({
  planGeometrySource,
  categoriesToRender,
  categoriesToRelax = categoriesToRender,
  categoryRenderOrder = viewportCategoryRenderOrder,
  relaxation,
  viewScale,
  filtering = new SheetGeometrySourceFilter({}, {}),
  styling = (geometry) => geometry,
  debugPrefs,
}: MakePlanGeometryListProps): Promise<SheetGeometryList> {
  const categoriesToRenderTable = categoriesToRender.reduce((acc, category) => {
    acc[category] = true;
    return acc;
  }, {} as SheetGeometryCategoryTable<true>);

  // Phase 1: filtering and styling
  class StyledFilteredPlanGeometrySource implements AbstractPlanGeometrySource {
    private geomTable: SheetGeometryCategoryTable<readonly TaggedPlanGeometry[]> = {};
    async getPlanGeometry(category: SheetGeometryCategory): Promise<readonly TaggedPlanGeometry[]> {
      if (this.geomTable[category]) {
        return this.geomTable[category];
      }
      if (categoriesToRenderTable[category] === undefined) {
        return [];
      }
      const categorySheetGeometry = await filtering.getFilteredPlanGeometry(category, planGeometrySource);

      this.geomTable[category] = styling(categorySheetGeometry, category);
      return this.geomTable[category];
    }
    async getGroupOfGeometries(category: SheetGeometryCategory): Promise<readonly GroupOfTaggedPlanGeometries[]> {
      // TODO: add filtering and styling to group of geometries
      return await filtering.getFilteredPlanGroups(category, planGeometrySource);
    }
  }

  const styledFilteredPlanGeometrySource = new StyledFilteredPlanGeometrySource();

  // Phase 2: prepopulate styled geometries in order.
  //          This is only necessary because of generator categories.
  //          After SDO-2037 this phase can be removed.
  const unrelaxedContentAABB = aabb2Init();
  for (const category of categoryRenderOrder) {
    if (categoriesToRenderTable[category] === undefined) {
      continue;
    }
    const geometries = await styledFilteredPlanGeometrySource.getPlanGeometry(category);
    if (viewScale === undefined) {
      const geometriesAABB = sheetGeometryListAABB2({ geometry: geometries });
      aabb2UnionMutable(unrelaxedContentAABB, geometriesAABB);
    }
  }

  // Phase 3: optional relaxation

  const relaxedPlanGeometryTable: SheetGeometryCategoryTable<readonly TaggedPlanGeometry[]> = {};

  if (relaxation) {
    const relaxer = relaxation(viewScale, unrelaxedContentAABB);
    for (const category of categoriesToRelax) {
      if (relaxedPlanGeometryTable[category] !== undefined) {
        continue;
      }
      // a category relaxer can relax multiple categories at once,
      // hence the type of `relaxedResult` is a table of categories to arrays of geometries
      // and not just an array of geometries.
      const relaxedResult: SheetGeometryCategoryTable<readonly SheetGeometry[]> = await relaxer(
        category,
        styledFilteredPlanGeometrySource,
        debugPrefs?.debugFloorPlanRelaxation,
      );
      for (const c in relaxedResult) {
        relaxedPlanGeometryTable[c] = relaxedResult[c];
      }
    }
  }

  // Phase 4: render in order
  const orderedPlanGeometryList = sheetGeometryListNew();
  let userDefinedAnnotations: TaggedPlanGeometry[] = [];

  for (const category of categoryRenderOrder) {
    if (!categoriesToRenderTable[category]) {
      continue;
    }

    let categorySheetGeometry = relaxedPlanGeometryTable[category];
    if (categorySheetGeometry) {
      // re-style the geoemtry that has been relaxed. This is not great, as some have already been styled,
      // but this function is likely to change with styling updates
      categorySheetGeometry = styling(categorySheetGeometry, category);
    } else {
      // If not relaxed, we can just get the already styled geoemtries from the source
      categorySheetGeometry = await styledFilteredPlanGeometrySource.getPlanGeometry(category);
    }

    if (isUserDefinedAnnotationCategory(category)) {
      userDefinedAnnotations.push(...categorySheetGeometry);
    } else {
      // We hit a category with non user defined annotations. Add the user defined annotations gathered so far.
      if (userDefinedAnnotations.length > 0) {
        userDefinedAnnotations.sort((a, b) => (a.renderOrder ?? 0) - (b.renderOrder ?? 0));
        sheetAddGeometries(orderedPlanGeometryList, userDefinedAnnotations);
        userDefinedAnnotations = [];
      }

      sheetAddGeometries(orderedPlanGeometryList, categorySheetGeometry);
    }
  }

  userDefinedAnnotations.sort((a, b) => (a.renderOrder ?? 0) - (b.renderOrder ?? 0));
  sheetAddGeometries(orderedPlanGeometryList, userDefinedAnnotations);

  return orderedPlanGeometryList;
}

export function supplyGeometryListTagIfMissing(
  geometry: SheetGeometry[],
  tag: SheetGeometryCategory,
): TaggedPlanGeometry[] {
  return geometry.map((g) => supplyGeometryTagIfMissing(g, tag));
}

export function supplyGeometryTagIfMissing(geometry: SheetGeometry, tag: SheetGeometryCategory): TaggedPlanGeometry {
  return geometry.category ? (geometry as TaggedPlanGeometry) : overrideGeometryTag(geometry, tag);
}

export function overrideGeometryListTags(geometry: SheetGeometry[], tag: SheetGeometryCategory): TaggedPlanGeometry[] {
  return geometry.map((g) => overrideGeometryTag(g, tag));
}

export function overrideGeometryTag(geometry: SheetGeometry, tag: SheetGeometryCategory): TaggedPlanGeometry {
  return { ...geometry, category: tag };
}
